import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Users, 
  Database, 
  ShoppingCart, 
  Bot, 
  Link as LinkIcon, 
  Settings 
} from 'lucide-react'

const Services = () => {
  const { t } = useTranslation()
  const services = [
    {
      icon: <Users className="w-8 h-8 text-purple-400" />,
      title: t('services.card1Title', "Lead Generation Automation"),
      description: t('services.card1Desc', "Collect leads from anywhere, enrich their data, score them by quality, and automatically sync them with your CRM — all on autopilot. Wake up to new qualified leads every morning."),
      features: t('services.card1Features', { returnObjects: true, defaultValue: ["Multi-source Collection", "Data Enrichment", "Lead Scoring", "CRM Sync"] })
    },
    {
      icon: <Database className="w-8 h-8 text-purple-400" />,
      title: t('services.card2Title', "CRM & Sales Automation"),
      description: t('services.card2Desc', "Sync contacts across platforms, automate email follow-ups, activate hot lead alerts, and generate sales reports — without lifting a finger."),
      features: t('services.card2Features', { returnObjects: true, defaultValue: ["Contact Sync", "Auto Follow-ups", "Lead Alerts", "Reports"] })
    },
    {
      icon: <ShoppingCart className="w-8 h-8 text-purple-400" />,
      title: t('services.card3Title', "E-commerce Automation"),
      description: t('services.card3Desc', "Order processing, inventory sync, customer notifications, shipping updates, and revenue reports — connected via Shopify, WooCommerce, Stripe, and more."),
      features: t('services.card3Features', { returnObjects: true, defaultValue: ["Order Automation", "Inventory Sync", "Customer Notifications", "Revenue Tracking"] })
    },
    {
      icon: <Bot className="w-8 h-8 text-purple-400" />,
      title: t('services.card4Title', "AI-Powered Workflows"),
      description: t('services.card4Desc', "Integrate ChatGPT and AI tools for automatic content generation, data summarization, lead scoring, and smart replies — 24/7."),
      features: t('services.card4Features', { returnObjects: true, defaultValue: ["Content Generation", "Data Summarization", "Lead Scoring", "Auto Replies"] })
    },
    {
      icon: <LinkIcon className="w-8 h-8 text-purple-400" />,
      title: t('services.card5Title', "Custom Integrations"),
      description: t('services.card5Desc', "Connect any tool to any tool. If it has an API, we can automate it. Google Sheets, Slack, Notion, Airtable, HubSpot, Salesforce, and 400+ more."),
      features: t('services.card5Features', { returnObjects: true, defaultValue: ["API Integrations", "Webhook Handling", "Data Transformation", "Multi-platform Sync"] })
    },
    {
      icon: <Settings className="w-8 h-8 text-purple-400" />,
      title: t('services.card6Title', "Workflow Maintenance"),
      description: t('services.card6Desc', "Already have n8n workflows? We optimize, fix, and maintain them so you never miss an opportunity. Keep everything running smoothly."),
      features: t('services.card6Features', { returnObjects: true, defaultValue: ["Bug Fixes", "Optimization", "Monitoring", "Updates"] })
    }
  ]

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.08,
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      },
    }),
  }

  return (
    <section id="services" className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0a0a0f] relative">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent"></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">{t('services.badge', 'Our Services')}</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 sm:mt-4 mb-4 sm:mb-6 text-white">
            {t('services.heading1', 'What We')} <span className="text-purple-400">{t('services.heading1b', 'Build')}</span> {t('services.heading1c', 'For You')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-400">
            {t('services.description', 'Comprehensive automation solutions that save time, reduce errors, and grow your business.')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              className="group p-6 sm:p-8 bg-white/5 border border-white/10 rounded-2xl hover:border-purple-500/50 transition-all duration-300"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
              <p className="text-gray-400 mb-6 text-sm leading-relaxed">{service.description}</p>
              
              <div className="flex flex-wrap gap-2">
                {service.features.map((feature, idx) => (
                  <span 
                    key={idx}
                    className="text-xs px-3 py-1.5 bg-purple-500/10 text-purple-300 rounded-full border border-purple-500/20"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Services